<template>
  <div class="pages-ccd-contact-detail">
    <div class="page-head-container">
      <div class="page-head clearfix">
        <div class="page-head-info columns clearfix">
          <div class="nav-bar">
            <a @click="navBack()">
              &lt; {{ $t('back') }}
            </a>
          </div>

          <div class="page-head-project-info clearfix">
            <div
              v-if="currentContact.id"
              class="page-project-number"
            >
              {{ $t('contact.contactNumber') }} {{ currentContact.id || '' }}
            </div>
            <div class="row page-project-name">
              <div>{{ title }}</div>
            </div>
          </div>
          <div class="float-right clearfix">
            <button
              v-if="isAllowLink"
              v-hkldreset
              type="button"
              class="btn btn--orange"
              @click="linkTo()"
            >
              {{ $t('action.link') }}
            </button>
            <button
              v-if="!allowEdit && allowEditContact"
              v-hkldreset
              type="button"
              class="btn btn--orange"
              @click="enableEdit()"
            >
              {{ $t('action.edit') }}
            </button>
            <button
              v-if="allowEdit"
              v-hkldreset
              type="button"
              class="btn btn--darkblue"
              @click="cancelEdit()"
            >
              {{ $t('action.cancel') }}
            </button>
            <button
              v-if="allowEdit"
              type="button"
              class="btn btn--orange"
              @click="save()"
            >
              {{ $t('action.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <ContactInformation
      :allowEdit="allowEdit"
      :contactId="contactId"
      :originalContact="keep"
      :inputRequireHighlight="inputRequireHighlight"
      :highlightAddressIndexes="highlightAddressIndexes"
      :highlightEmail="highlightEmail"
      :highlightPhone="highlightPhone"
    />
    <ContactCompany
      :allowEdit="allowEdit"
      :contactId="contactId"
      :contactCompany="contactCompany"
    />
    <CompanyTenancyRecord
      v-if="isEngineer && enableTenancyRecordsFeature"
      :allowCreate="allowEdit"
      :showCreateTenancy="false"
      :tenancies="tenancies"
    />
    <ActionConfirmModal
      ref="confirmModalBeforeSaving"
      :showCancelBtn="showACModalCancelBtn"
      :confirmTitle="confirmMessage"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { ref, computed } from 'vue-demi'
import ContactInformation from '@/modules/ccd/contacts/ContactInformation'
import ContactCompany from '@/modules/ccd/contacts/ContactCompany'
import CompanyTenancyRecord from '@/modules/ccd/companies/CompanyTenancyRecord'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import mutationTypes from '@/store/mutation-types'
import formFullnameMixin from '@/pages/formFullnameMixin'
import navigation, { navToFromCompanyPageWithEdit } from '@/utils/navigation'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { ccdRouteNames } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import EventBus from '@/utils/eventBus'
import { findContactsRouteByUserType, findEditContactRouteByUserType, findViewContactRouteByUserType } from '@/utils/ccdUtil'
import { CCD_EMAIL_LINK_TENANCY_TYPES, CCD_PHONE_LINK_TENANCY_TYPES, TENANCY_ALL_CONTACT_TYPES, TENANCY_ALL_CONTACT_TYPES_MAP } from '@/modules/ccd/tenancy/consts'
import { useStoreGetters } from '@/store/index'
import useRouter from '@/router/useRouter'
import { checkIsLinkedTrPhone, getLinkedTrEmails } from './tenancy.utils'

export default {
  name: 'CcdContactDetail',
  components: {
    ActionConfirmModal,
    ContactInformation,
    ContactCompany,
    CompanyTenancyRecord,
  },
  mixins: [formFullnameMixin],
  setup () {
    const highlightEmail = ref(false)
    const highlightPhone = ref(false)
    const highlightAddressIndexes = ref([])
    const inputRequireHighlight = ref(false)
    const { currentContact } = useStoreGetters('ccd', ['currentContact'])
    const isInvalidValues = (fieldNames) => {
      let result = true
      for (let i = 0; i < fieldNames.length; i++) {
        if (currentContact.value[fieldNames[i]].some(field => field.value)) {
          result = false
        }
      }
      return result
    }
    const validateAddresses = () => {
      const fields = ['addressLine1', 'addressLine2', 'addressLine3', 'addressLine4']
      highlightAddressIndexes.value = []
      currentContact.value['addresses'].forEach((item, index) => {
        if (fields.every(attr => !item.value[attr])) {
          highlightAddressIndexes.value.push(index)
        }
      })
    }
    const hasAtLeastOneContact = () => {
      inputRequireHighlight.value = false
      highlightEmail.value = false
      const mustInputEmails = CCD_EMAIL_LINK_TENANCY_TYPES.some(field => query.value[field])
      const mustInputPhone = CCD_PHONE_LINK_TENANCY_TYPES.some(field => query.value[field])
      const linkTrContactChecking = mustInputEmails || mustInputPhone
      if (linkTrContactChecking) {
        highlightEmail.value = mustInputEmails && isInvalidValues(['emails'])
        highlightPhone.value = mustInputPhone && isInvalidValues(['phoneNumbers'])
        inputRequireHighlight.value = false
      } else {
        inputRequireHighlight.value = isInvalidValues(['faxNumbers', 'emails', 'phoneNumbers'])
      }

      validateAddresses()
      return !inputRequireHighlight.value && !highlightEmail.value && !highlightPhone.value && !highlightAddressIndexes.value.length
    }
    const removeLinkTrQueryAfterCreating = (queryVal) => {
      TENANCY_ALL_CONTACT_TYPES.forEach(type => {
        delete queryVal[type]
      })
    }
    const { route } = useRouter()
    const query = computed(() => route.value.query)
    const allowEdit = ref(false)
    const isFieldsValidateForLink = computed(() => {
      const hasContactEmails = currentContact.value.emails.length > 0
      const hasContactPhones = currentContact.value.phoneNumbers.length > 0
      const mustInputEmails = CCD_EMAIL_LINK_TENANCY_TYPES.some(field => query.value[field])
      const mustInputPhones = CCD_PHONE_LINK_TENANCY_TYPES.some(field => query.value[field])
      if (mustInputEmails && mustInputPhones) {
        return hasContactEmails && hasContactPhones
      } else if (mustInputEmails) {
        return hasContactEmails
      } else if (mustInputPhones) {
        return hasContactPhones
      }
      return true
    })
    const isAllowLink = computed(() => {
      return !allowEdit.value && query.value.allowLink && isFieldsValidateForLink.value
    })
    return {
      isAllowLink,
      isFieldsValidateForLink,
      allowEdit,
      query,
      highlightEmail,
      highlightPhone,
      highlightAddressIndexes,
      inputRequireHighlight,
      isInvalidValues,
      validateAddresses,
      hasAtLeastOneContact,
      removeLinkTrQueryAfterCreating,
    }
  },
  data () {
    return {
      hasSave: false,
      description: this.$t('contacts.createNew'),
      keep: {},
      contactCompany: {},
      contactId: null,
      confirmMessage: '',
      showACModalCancelBtn: true,
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentContact', 'currentTenancy', 'canTenantEditContact', 'canVenodrCreateUpadteContact']),
    ...mapGetters('user', ['isTenant', 'currentUser', 'isEngineer']),
    ...mapGetters('regions', ['enableTenancyRecordsFeature']),
    allowEditContact () {
      return this.canTenantEditContact || this.canVenodrCreateUpadteContact || aclHelper.getAccess(ccdPermsTypes.contact.createUpdate)
    },
    tenancies () {
      if (this.contactId) {
        return this.currentContact.tenancies || []
      } else if (this.query && this.query.tenancyId) {
        return [this.currentTenancy]
      }
      return []
    },
    title () {
      const [firstName, lastName] = [
        lodash.get(this.currentContact, 'firstName'),
        lodash.get(this.currentContact, 'lastName'),
      ]
      return firstName ? this.displayI18nFullName(firstName, lastName) : this.description
    },
  },
  beforeMount () {
    this.contactId = this.$router.currentRoute.params.id
  },
  async mounted () {
    const routeName = this.$router.currentRoute.name
    const isViewRoute = [ccdRouteNames.ccdContactView, ccdRouteNames.tenantCcdContactView, ccdRouteNames.vendorCcdContactView].includes(routeName)
    const isEditRoute = [ccdRouteNames.ccdContactEdit, ccdRouteNames.tenantCcdContactEdit, ccdRouteNames.vendorCcdContactEdit].includes(routeName)
    if (isViewRoute) {
      await this.loadContact(this.contactId)
    } else if (isEditRoute) {
      await this.loadContact(this.contactId)
      this.keep = lodash.cloneDeep(this.currentContact)
      this.allowEdit = true
    } else {
      this.resetCurrentContact()
      this.allowEdit = true
    }
    EventBus.$on('checkInputRequireHighlight', this.hasAtLeastOneContact)
  },
  methods: {
    ...mapActions('ccd', [
      'loadContact',
      'createContact',
      'updateContact',
      'linkContactsToCompany',
      'loadTenancy',
      'linkContactsToTenancy',
    ]),
    ...mapMutations('ccd', {
      setCurrentContact: mutationTypes.CONTACT_UPDATE_CURRENT,
      resetCurrentContact: mutationTypes.CONTACT_RESET_CURRENT,
    }),
    enableEdit () {
      this.navToCddContact(true)
      this.keep = lodash.cloneDeep(this.currentContact)
      this.allowEdit = true
    },
    cancelEdit () {
      formUtils.resetDirty()
      const isCreatePage = !this.contactId && !lodash.isEmpty(this.query)
      if (isCreatePage) {
        const routeName = findContactsRouteByUserType()
        navigation.toPage(routeName, {}, this.query)
      } else {
        this.allowEdit = false
        this.inputRequireHighlight = false
        this.setCurrentContact({ contact: this.keep })
        this.navToCddContact()
      }
    },
    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'partial'}, '.pages-ccd-contact-detail')
      return $('.pages-ccd-contact-detail input.is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.pages-ccd-contact-detail')
    },
    diffTenancyValues (currentMap, keepMap) {
      const diffValues = lodash.reduce(keepMap, (result, item) => {
        const currentValue = lodash.get(currentMap, item.id)
        const isValueDeleted = !currentValue
        const isValueUpdated = currentValue && item.value !== currentValue.value
        if (isValueDeleted ||
          (isValueUpdated)) {
          result[item.id] = item.value
        }
        return result
      }, {})
      return diffValues
    },
    diffVendorUpdatedValues (currentMap, keepMap) {
      const diffValues = lodash.reduce(keepMap, (result, item) => {
        const currentValue = lodash.get(currentMap, item.id)
        if (currentValue && item.value !== currentValue.value) {
          result[item.id] = item.value
        }
        return result
      }, {})
      return diffValues
    },
    dispatchDiffFn (currentMap, keepMap) {
      const isTenancyContact = this.tenancies.length
      const result = isTenancyContact ? this.diffTenancyValues(currentMap, keepMap) : this.diffVendorUpdatedValues(currentMap, keepMap)
      return result
    },
    diffPhones () {
      const keepPhoneMap = lodash.keyBy(this.keep.phoneNumbers, 'id')
      const currentPhoneMap = lodash.keyBy(this.currentContact.phoneNumbers, 'id')
      const diffPhoneNumbers = this.dispatchDiffFn(currentPhoneMap, keepPhoneMap)
      return Object.keys(diffPhoneNumbers).length !== 0
    },
    validateEmailsIsTrContacts () {
      const linkedEmails = getLinkedTrEmails(
        this.currentContact.tenancies,
        CCD_EMAIL_LINK_TENANCY_TYPES,
        TENANCY_ALL_CONTACT_TYPES_MAP,
      )
      const keepEmailMap = lodash.keyBy(this.keep.emails, 'id')
      const currentEmailMap = lodash.keyBy(this.currentContact.emails, 'id')
      const diffEmails = this.dispatchDiffFn(currentEmailMap, keepEmailMap)
      return lodash.some(diffEmails, value => linkedEmails.includes(value))
    },
    diffVendorEmails () {
      const keepEmailsMap = lodash.keyBy(this.keep.emails, 'id')
      const currentEmailsMap = lodash.keyBy(this.currentContact.emails, 'id')
      const diffEmails = this.dispatchDiffFn(currentEmailsMap, keepEmailsMap)
      return Object.keys(diffEmails).length !== 0
    },
    validateDuplicatedValue () {
      const validatePropetiesMap = {
        'phoneNumbers': 'contact.phone.phoneNumber',
        'emails': 'contact.email.emailAddr',
        'faxNumbers': 'contact.fax.fax',
      }
      let duplicatedPropeties = []
      lodash.forEach(validatePropetiesMap, (value, key) => {
        const data = lodash.get(this.currentContact, key)
        const values = data.map(item => item.value)
        if (lodash.uniq(values).length !== values.length) {
          duplicatedPropeties.push(value)
        }
      })
      return duplicatedPropeties
    },
    async linkTo () {
      if (this.query && this.query.tenancyId) {
        this.navToTenancyPage()
      } else if (this.query && this.query.companyId) {
        await this.linkToCompany()
      }
    },
    getContactsByType (type) {
      const tenancies = this.currentContact.tenancies || []
      return tenancies.filter(tenancy =>
        tenancy.CompanyContact && tenancy.CompanyContact[type])
    },

    async linkToCompany () {
      if (!this.currentContact.companies.some(item => item.id === this.query.companyId)) {
        await this.linkContactsToCompany({
          companyId: this.query.companyId,
          request: [this.currentContact.id],
        })
      }
      navToFromCompanyPageWithEdit(this.query)
    },
    async createCcdContact () {
      let query = lodash.cloneDeep(this.query)
      this.removeLinkTrQueryAfterCreating(query)
      const contact = await this.createContact({ companyContact: { ...query, ...this.contactCompany } })
      if (!contact) {
        return
      }
      this.contactId = contact.id
      this.allowEdit = false
      await this.loadContact(this.contactId)
    },
    async updateCcdContact () {
      const request = lodash.cloneDeep(this.currentContact)
      const companies = request.companies
      delete request.companies
      const companyIds = companies.map(company => company.id)

      const companyToDeleteIds = []
      this.keep.companies.map(company => {
        if (!companyIds.includes(company.id)) {
          companyToDeleteIds.push(company.id)
        }
      })
      request.tenancyId = this.query && this.query.tenancyId
      request.previousContact = this.keep
      request.currentContact = this.currentContact
      const updateSuccess = await this.updateContact({ contactId: this.contactId, request })
      if (updateSuccess) {
        await this.loadContact(this.contactId)
        this.keep = lodash.cloneDeep(this.currentContact)
        this.allowEdit = false
        return true
      }
      return false
    },
    navToTenancyPage () {
      const routeOptions = {
        name: ccdRouteNames.ccdTenancyEdit,
        params: { id: this.query.tenancyId },
      }
      const isMarketing = aclHelper.getAccess(ccdPermsTypes.tenancy.manageMarketingContacts) && !aclHelper.getAccess(ccdPermsTypes.tenancy.linkContacts)
      const linkType = TENANCY_ALL_CONTACT_TYPES.find(attr => this.query[attr])
      if (linkType) {
        routeOptions.params.type = linkType
        routeOptions.params.ccdContactIds = [this.currentContact.id]
      }
      if (this.isTenant || isMarketing) {
        routeOptions.name = this.isTenant ? ccdRouteNames.ccdTenancyTenantView : ccdRouteNames.ccdTenancyView
        routeOptions.params.enableContactEdit = true
      } else {
        routeOptions.query = { companyId: this.query.companyId }
      }
      this.$router.replace(routeOptions)
      EventBus.emit('common-tabs-container-select-tab', '#tab-1', 1)
    },
    navToCddContact (isEdit) {
      const name = isEdit ? findEditContactRouteByUserType() : findViewContactRouteByUserType()
      this.$router.replace({
        name,
        params: { id: this.contactId },
        query: this.query,
      })
    },
    openModal (confirmMessage, confirmAction, params = []) {
      this.confirmMessage = confirmMessage
      this.$refs.confirmModalBeforeSaving.open(confirmAction, params)
    },
    async save () {
      fusionInputCheckUtil.resetAllFields()
      const validateInput = this.validate()
      if (!validateInput) return

      this.showACModalCancelBtn = true
      const duplicatedPropeties = this.validateDuplicatedValue()
      if (duplicatedPropeties.length) {
        this.showACModalCancelBtn = false
        let alertMsg = ''
        duplicatedPropeties.forEach(value => {
          alertMsg += this.$t('hint.contact.hintDuplicatedValue', [this.$t(value)]) + '</br>'
        })
        this.openModal(alertMsg)
      } else {
        const hasLinkedTrContactsEmails = this.validateEmailsIsTrContacts()
        const ifVendorEmailsDiff = this.diffVendorEmails()
        const ifEmailRceECircular = ifVendorEmailsDiff && (this.currentContact.companies || []).some(company => company.CompanyContact.companyId && company.CompanyContact.isVendorCircular)

        const ifPhonesDiff = this.diffPhones()
        const hasLinkedTrContactsPhone = ifPhonesDiff && checkIsLinkedTrPhone(this.currentContact.tenancies, CCD_PHONE_LINK_TENANCY_TYPES)
        const isPhoneLinkedVendor = ifPhonesDiff && !this.tenancies.length
        let hintMsg
        if (hasLinkedTrContactsEmails && !hasLinkedTrContactsPhone) {
          hintMsg = this.$t('hint.contact.hintLinkedTrEmail')
        } else if (!hasLinkedTrContactsEmails && hasLinkedTrContactsPhone) {
          hintMsg = this.$t('hint.contact.hintLinkedTrPhone')
        } else if (hasLinkedTrContactsEmails && hasLinkedTrContactsPhone) {
          hintMsg = this.$t('hint.contact.hintLinkedTrEmailAndPhone')
        } else if (ifEmailRceECircular) {
          hintMsg = this.$t('hint.contact.email.remindTheUpdate')
        } else if (isPhoneLinkedVendor) {
          hintMsg = this.$t('hint.contact.phoneNum.remindTheUpdate')
        }
        if (!hintMsg) {
          await this.submit()
          return
        }
        this.openModal(hintMsg, this.submit)
      }
    },
    async submit () {
      const validateContactLength = this.hasAtLeastOneContact()
      if (!validateContactLength) {
        return false
      }
      if (!this.contactId) {
        await this.createCcdContact()
        formUtils.resetDirty()
        if (this.query && (this.query.tenancyId || this.query.companyId)) {
          this.navBack()
        }
      } else {
        const success = await this.updateCcdContact()
        if (!success) {
          return
        }
        this.navToCddContact()
        formUtils.resetDirty()
      }
      EventBus.emit('update_histories_table')
    },
    navBack () {
      this.allowEdit = false
      this.$router.goBack()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/common-header";
@import "~styles/popup";
</style>
